import React, { useEffect, useRef } from "react";
import { useInView, useMotionValue, useSpring } from "framer-motion";

/**
 * CountUp — animates a number from 0 to `to` when it scrolls into view.
 * `prefix`/`suffix` let you render things like "50+" or "5.0★".
 * If `to` isn't a finite number, children/`display` are rendered as-is.
 */
const CountUp = ({ to, prefix = "", suffix = "", decimals = 0, display }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { duration: 1600, bounce: 0 });

  useEffect(() => {
    if (inView && Number.isFinite(to)) motionValue.set(to);
  }, [inView, to, motionValue]);

  useEffect(() => {
    if (!Number.isFinite(to)) return;
    return spring.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = `${prefix}${latest.toFixed(decimals)}${suffix}`;
      }
    });
  }, [spring, prefix, suffix, decimals, to]);

  // Non-numeric stat (e.g. "Top Rated") — render the label directly.
  if (!Number.isFinite(to)) {
    return <span ref={ref}>{display ?? `${prefix}${suffix}`}</span>;
  }

  return <span ref={ref}>{`${prefix}0${decimals ? "." + "0".repeat(decimals) : ""}${suffix}`}</span>;
};

export default CountUp;
